/** @jsx jsx */
import { jsx, useTheme } from '@emotion/react'
import {
    AppState,
    AuthenticationState,
    ConsentLevel,
    Product,
    ScriptLoadResult,
    useScriptWithConsent,
} from '@news-mono/web-common'
import React, { useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { ContentSeparator } from '../content/ContentSeparator/ContentSeparator.styled'
import { metrics } from '../__styling/settings/metrics'
import { calcRem } from '../__styling/style-functions/calc-rem'
import { SevenNewsAuthenticationContext } from '../SevenNewsV2/authentication/SevenNewsAuthenticationProvider'
import { CoralProps } from './Coral'
import { CoralEventStream, handleCoralEvents } from './coral-event-stream'

/**
 * --------------------------------------
 * Coral Documentation can be found here:
 * https://docs.coralproject.net/cms
 * --------------------------------------
 */
declare const Coral: CoralScript

interface CoralStreamEmbedProps {
    storyID?: string
    storyURL?: string
    commentID?: string
    rootURL?: string
    id?: string
    autoRender?: boolean
    accessToken?: string
    enableDeprecatedEvents?: boolean
    customCSSURL?: string

    /** Allow setting className of body tag inside iframe */
    containerClassName?: string
    events?: (eventStream: CoralEventStream) => void
}

/**
 * Controls returned by the coral script.
 */
type CoralEmbed = {
    login: (accessToken: string) => void
    logout: () => void
}

interface CoralScript {
    createStreamEmbed: (props: CoralStreamEmbedProps) => CoralEmbed
}

function checkCoral() {
    return typeof Coral !== 'undefined' && Coral && Coral.createStreamEmbed
}

function getSignInUrlWithRedirect() {
    const currentPage = `${window.location.pathname}${window.location.search}${window.location.hash}`

    return `/sign-in?redirectUrl=${encodeURIComponent(currentPage)}`
}

export interface CoralCommentsProps extends Omit<CoralProps, 'userSettings'> {
    coralScriptUrl: string
    hostName: string
    useContentSeperator: boolean
    canComment?: boolean
}

export const CoralComments: React.FC<CoralCommentsProps> = ({
    coralScriptUrl,
    storyID,
    storyURL,
    horizontalSpacing,
    onCommentEvent: onEvent,
    hostName,
    useContentSeperator,
    canComment = true,
}) => {
    const { kind } = useTheme()
    // Only mounted for SevenNews; other mastheads don't provide this context.
    const sevenNewsAuth = useContext(SevenNewsAuthenticationContext)
    const [result] = useScriptWithConsent({
        consentRequiredLevel: ConsentLevel.Essential | ConsentLevel.Analytics,
        consentMet: { src: `${coralScriptUrl}/assets/js/embed.js` },
    })

    const [streamLoaded, updateStreamLoaded] = useState(false)

    const authState = useSelector((state: AppState) => state.authentication)

    const renditionType = useSelector(
        (state: AppState) => state.render.renditionType,
    )

    // A workaround for a Safari bug which does not calculate the correct size on initial draw.
    const [display, setDisplay] = useState('block')

    const { coralToken, hasAttemptedValidation, checkingTokenState } = authState

    React.useEffect(() => {
        // Wait for the initial auth reload to settle so the stream isn't
        // created before/after coralToken lands, causing a duplicate embed.
        const attemptedValidation =
            kind === Product.SevenNews
                ? sevenNewsAuth?.isAuthReady ?? false
                : hasAttemptedValidation
        const loadStream =
            result === ScriptLoadResult.Success &&
            checkCoral() &&
            ((coralToken && kind === Product.TheWest) ||
                kind === Product.PerthNow ||
                kind === Product.TheNightly ||
                kind === Product.SevenNews) &&
            attemptedValidation &&
            !checkingTokenState

        // This allows us to insert some extra styles into the styleSheet that is used to style the Coral embed.
        const viewOnly =
            (!coralToken &&
                (kind === Product.PerthNow || kind === Product.TheNightly)) ||
            !canComment

        if (renditionType === 'app') {
            const redactedAuthState: AuthenticationState = {
                ...authState,
                userEmail: 'redacted',
                userName: 'redacted',
                wanUserId:
                    authState.wanUserId !== 'app-bad-token'
                        ? 'redacted'
                        : authState.wanUserId,
                auth0UserId: authState.auth0UserId ? 'redacted' : undefined,
            }

            console.info('INC-251 hasDuplicates', {
                auth0UserId: redactedAuthState.auth0UserId,
                wanUserId: redactedAuthState.wanUserId,
                coralToken: redactedAuthState.coralToken,
                canLoadStream: loadStream,
            })
        }

        if (loadStream) {
            Coral.createStreamEmbed({
                id: 'coral_thread',
                autoRender: true,
                rootURL: coralScriptUrl,
                accessToken: coralToken,
                containerClassName: `${kind}-comments`,
                customCSSURL: `${hostName}/coral.css${
                    viewOnly ? '?viewOnly=true' : ''
                }`,
                storyID,
                storyURL,
                events: function (eventStream) {
                    eventStream.onAny((event) => {
                        if (
                            kind == Product.SevenNews &&
                            event === 'loginPrompt'
                        ) {
                            // Redirect user to a login page.
                            void sevenNewsAuth?.logoutUser()
                            window.location.href = getSignInUrlWithRedirect()
                        }
                        if (
                            kind == Product.SevenNews &&
                            event === 'signOut.success'
                        ) {
                            void sevenNewsAuth?.logoutUser()
                        }
                        if (event === 'ready') {
                            // Force safari to redraw.
                            setDisplay('none')
                            setTimeout(() => {
                                setDisplay('block')
                            })

                            //switch to all comments tab
                            setTimeout(() => {
                                const shadowRootContainer =
                                    document.getElementById(
                                        'coral-shadow-container',
                                    )?.shadowRoot

                                const updateSignInButtonLabel = () => {
                                    if (kind !== Product.SevenNews) return

                                    const signInButton =
                                        shadowRootContainer?.querySelector(
                                            '.coral.coral-createComment-signIn',
                                        )

                                    if (
                                        signInButton &&
                                        signInButton.textContent !==
                                            'Sign in to join the conversation'
                                    ) {
                                        signInButton.textContent =
                                            'Sign in to join the conversation'
                                    }
                                }

                                const updateMostRespectedSortLabel = () => {
                                    if (kind !== Product.SevenNews) return

                                    const mostRespectedOption =
                                        shadowRootContainer?.querySelector(
                                            '#coral-comments-sortMenu option[value="REACTION_DESC"]',
                                        )

                                    if (
                                        mostRespectedOption?.textContent ===
                                        'Most Respected'
                                    ) {
                                        mostRespectedOption.textContent =
                                            'Most respected'
                                    }
                                }

                                const observer = new MutationObserver(() => {
                                    updateSignInButtonLabel()
                                    updateMostRespectedSortLabel()

                                    const featuredWindowComponent =
                                        shadowRootContainer?.querySelector(
                                            '.coral.coral-featuredComments',
                                        )

                                    if (!featuredWindowComponent) return

                                    const allCommentsInnerButton =
                                        shadowRootContainer?.querySelector(
                                            '.coral.coral-tabBarComments-allComments',
                                        ) as HTMLElement
                                    allCommentsInnerButton?.click()
                                })

                                if (shadowRootContainer) {
                                    observer.observe(shadowRootContainer, {
                                        subtree: true,
                                        childList: true,
                                    })
                                    updateSignInButtonLabel()
                                    updateMostRespectedSortLabel()
                                }
                            }, 500)
                        }
                    })
                    handleCoralEvents(eventStream, onEvent)
                },
            })
            updateStreamLoaded(true)
        }
    }, [
        result,
        coralScriptUrl,
        kind,
        storyID,
        storyURL,
        onEvent,
        coralToken,
        hasAttemptedValidation,
        checkingTokenState,
        sevenNewsAuth?.isAuthReady,
        canComment,
        renditionType,
        authState,
        hostName,
    ])

    return (
        <React.Fragment>
            <div
                id="coral_thread"
                className="hide-print"
                css={{
                    display: display,
                    margin: `0 ${
                        horizontalSpacing &&
                        calcRem(metrics.thewest.margins[horizontalSpacing])
                    }`,
                }}
            />
            {useContentSeperator && streamLoaded && (
                <ContentSeparator verticalBottomSpacing="lg" />
            )}
        </React.Fragment>
    )
}

export default CoralComments
